import React, { useState, useEffect } from 'react';
import { Row, Col, Form, Button, InputGroup, Badge } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faTrash, faFilter, faTimes, faSave } from '@fortawesome/free-solid-svg-icons';
import moment from 'moment';

const OPERATORS = [
    { label: 'Contiene', value: 'LIKE' },
    { label: 'Es igual a', value: '=' },
    { label: 'Es distinto a', value: '!=' },
    { label: 'Mayor que', value: '>' },
    { label: 'Menor que', value: '<' },
    { label: 'Mayor o igual que', value: '>=' },
    { label: 'Menor o igual que', value: '<=' },
    { label: 'Comienza con', value: 'START' },
    { label: 'Termina con', value: 'END' },
];

export default function SmartFilter({ columns, onFilter, moduleName }) {
    const [rules, setRules] = useState([]);
    const [discoveredColumns, setDiscoveredColumns] = useState([]);
    const storageKey = `dm_filter_${moduleName || 'default'}`;

    useEffect(() => {
        const saved = localStorage.getItem(storageKey);
        if (saved) {
            try {
                const parsed = JSON.parse(saved);
                setRules(parsed);
                onFilter(parsed);
            } catch (e) { console.error("Error loading filters", e); }
        }
    }, [moduleName]);

    useEffect(() => {
        if (columns && columns.length > 0) {
            setDiscoveredColumns(columns);
        }
    }, [columns]);

    const addRule = () => {
        const firstCol = discoveredColumns[0]?.key || '';
        setRules([...rules, { column: firstCol, operator: 'LIKE', value: '', logic: 'AND' }]);
    };

    const removeRule = (index) => {
        const newRules = rules.filter((_, i) => i !== index);
        setRules(newRules);
        if (newRules.length === 0) onFilter([]);
    };

    const updateRule = (index, field, val) => {
        const newRules = [...rules];
        newRules[index][field] = val;
        setRules(newRules);
    };

    const applyFilters = () => {
        onFilter(rules);
        localStorage.setItem(storageKey, JSON.stringify(rules));
    };

    const clearFilters = () => {
        setRules([]);
        onFilter([]);
        localStorage.removeItem(storageKey);
    };

    return (
        <div className="smart-filter-container p-3 border rounded bg-light mb-3 animate__animated animate__fadeIn">
            <div className="d-flex justify-content-between align-items-center mb-3">
                <h6 className="mb-0 font-weight-bold text-primary">
                    <FontAwesomeIcon icon={faFilter} className="mr-2" />
                    Constructor de Filtros Inteligente
                </h6>
                <div className="d-flex gap-2">
                    <Button variant="outline-danger" size="sm" onClick={clearFilters} disabled={rules.length === 0}>
                        <FontAwesomeIcon icon={faTimes} className="mr-1" /> Limpiar
                    </Button>
                    <Button variant="primary" size="sm" onClick={applyFilters}>
                        Aplicar Filtros
                    </Button>
                </div>
            </div>

            {rules.length === 0 ? (
                <div className="text-center py-4 text-muted border-dashed rounded">
                    <p className="mb-2">No hay filtros activos. Crea uno para empezar el análisis.</p>
                    <Button variant="outline-primary" size="sm" onClick={addRule}>
                        <FontAwesomeIcon icon={faPlus} className="mr-1" /> Añadir primer filtro
                    </Button>
                </div>
            ) : (
                <div className="rules-list">
                    {rules.map((rule, index) => (
                        <Row key={index} className="mb-2 align-items-center g-2 animate__animated animate__fadeInLeft" style={{animationDelay: `${index * 0.05}s`}}>
                            {index > 0 && (
                                <Col xs={12} className="text-center my-1">
                                    <Badge bg="info" style={{fontSize: '0.65rem'}}>Y (AND)</Badge>
                                </Col>
                            )}
                            <Col md={4}>
                                <Form.Select 
                                    size="sm" 
                                    value={rule.column} 
                                    onChange={(e) => updateRule(index, 'column', e.target.value)}
                                    className="sa-input"
                                >
                                    {discoveredColumns.map(c => (
                                        <option key={c.key} value={c.key}>{c.label || c.key}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={3}>
                                <Form.Select 
                                    size="sm" 
                                    value={rule.operator} 
                                    onChange={(e) => updateRule(index, 'operator', e.target.value)}
                                    className="sa-input"
                                >
                                    {OPERATORS.map(op => (
                                        <option key={op.value} value={op.value}>{op.label}</option>
                                    ))}
                                </Form.Select>
                            </Col>
                            <Col md={4}>
                                <Form.Control 
                                    size="sm" 
                                    type="text" 
                                    placeholder="Valor..." 
                                    value={rule.value}
                                    onChange={(e) => updateRule(index, 'value', e.target.value)}
                                    className="sa-input"
                                    onKeyUp={(e) => e.key === 'Enter' && applyFilters()}
                                />
                            </Col>
                            <Col md={1} className="text-end">
                                <Button variant="link" className="text-danger p-0" onClick={() => removeRule(index)}>
                                    <FontAwesomeIcon icon={faTrash} />
                                </Button>
                            </Col>
                        </Row>
                    ))}
                    <div className="mt-3">
                        <Button variant="outline-primary" size="sm" onClick={addRule} className="rounded-pill">
                            <FontAwesomeIcon icon={faPlus} className="mr-1" /> Añadir Condición
                        </Button>
                    </div>
                </div>
            )}
        </div>
    );
}
